import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { MorrisonsDay, MorrisonsPayload, MorrisonsWeek } from '../report';
import { fmtCompact, fmtDay, fmtInt, fmtPct, niceScale, share } from '../format';
import { weekStickers } from '../derive';

/*
 * The design's four drawings, plain SVG: stickers per day (every scan — new
 * cards and duplicates, camera and manual — the running day in red, a dashed
 * rule where each week starts), stickers per week, the prize donut and the 120-card heat
 * grid with the five rarest cards outlined. Every bar has a tooltip; the daily
 * chart also reads with the arrow keys.
 */

/** Width of a box, following resizes (falls back to 800 where ResizeObserver is missing). */
function useWidth<T extends HTMLElement>(fallback = 800) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(([e]) => setWidth(Math.max(280, e.contentRect.width)));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, width] as const;
}

/** A column from the baseline up to `top` with a rounded data end; null when it has no height. */
function barPath(x: number, top: number, w: number, base: number, round = true): string | null {
  const h = base - top;
  if (h <= 0) return null;
  const r = round ? Math.min(4, w / 2, h) : 0;
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${base}Z`;
}

const H = 260;
const PAD = { l: 48, r: 12, t: 28, b: 28 };

export function DailyStickersChart({ days }: { days: MorrisonsDay[] }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const measured = days.filter((d) => d.newCards != null);
  const lastIdx = days.reduce((a, d, i) => (d.newCards != null ? i : a), -1);
  // The chart runs to the Sunday of the running week, like the design.
  const shown = days.slice(0, Math.min(days.length, lastIdx < 0 ? 7 : Math.ceil((lastIdx + 1) / 7) * 7));
  const total = (d: MorrisonsDay) => (d.newCards ?? 0) + (d.duplicateScans ?? 0);
  const { max, step } = niceScale(Math.max(0, ...shown.map(total)));
  const innerW = width - PAD.l - PAD.r;
  const base = H - PAD.b;
  const y = (v: number) => base - (v / max) * (base - PAD.t);
  const band = innerW / Math.max(1, shown.length);
  const left = (i: number) => PAD.l + band * i;
  const bw = Math.max(2, Math.min(22, band * 0.72));
  const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_v, i) => i * step);
  const every = width < 480 ? 7 : width < 800 ? 3 : 2;

  if (measured.length === 0) return <p className="mo-note">The daily figures appear after the first pipeline run.</p>;

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setHover((h) => Math.min(lastIdx, Math.max(0, (h ?? lastIdx) + (e.key === 'ArrowRight' ? 1 : -1))));
  }
  const active = hover != null ? shown[hover] : null;
  const current = shown[hover ?? lastIdx];
  const label = (d: MorrisonsDay) => `${fmtDay(d.date, 'long')}: ${fmtInt(total(d))} scans`;

  return (
    <div
      ref={ref}
      className="mo-chart"
      role="slider"
      aria-label="Stickers scanned per day — use the arrow keys to read each day"
      aria-valuemin={0}
      aria-valuemax={Math.max(0, lastIdx)}
      aria-valuenow={Math.max(0, hover ?? lastIdx)}
      aria-valuetext={current ? label(current) : undefined}
      tabIndex={0}
      onKeyDown={onKey}
      onFocus={() => setHover((h) => h ?? lastIdx)}
      onBlur={() => setHover(null)}
      onPointerMove={(e) => {
        const rect = ref.current?.getBoundingClientRect();
        const i = Math.floor((e.clientX - (rect?.left ?? 0) - PAD.l) / band);
        setHover(Math.min(lastIdx, Math.max(0, i)));
      }}
      onPointerLeave={() => setHover(null)}
    >
      <svg width={width} height={H} aria-hidden="true">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.l} x2={width - PAD.r} y1={y(t)} y2={y(t)} className="mo-gridline" />
            <text x={PAD.l - 8} y={y(t) + 4} textAnchor="end">
              {t === 0 ? '0' : fmtCompact(t)}
            </text>
          </g>
        ))}
        {shown.map((d, i) =>
          i % 7 === 0 ? (
            <g key={`w${d.date}`}>
              {i > 0 && <line x1={left(i)} x2={left(i)} y1={PAD.t - 14} y2={base} className="mo-week-rule" />}
              <text x={left(i) + 4} y={PAD.t - 10} className="mo-week-label">
                {width < 520 ? `Wk${i / 7 + 1}` : `Week ${i / 7 + 1}`}
              </text>
            </g>
          ) : null,
        )}
        {active && <rect x={left(hover ?? 0)} y={PAD.t} width={band} height={base - PAD.t} className="mo-hover-band" />}
        {shown.map((d, i) => {
          if (d.newCards == null) return null;
          const x = left(i) + (band - bw) / 2;
          const bar = barPath(x, y(total(d)), bw, base);
          return bar && <path key={d.date} d={bar} className={i === lastIdx ? 'mo-bar-hot' : 'mo-bar-new'} />;
        })}
        {shown.map((d, i) =>
          i % every === 0 ? (
            <text key={`x${d.date}`} x={left(i) + band / 2} y={H - 8} textAnchor="middle">
              {fmtDay(d.date, 'dayMonth')}
            </text>
          ) : null,
        )}
      </svg>
      {active && active.newCards != null && (
        <div className={`mo-tip${left(hover ?? 0) + 220 > width ? ' flip' : ''}`} style={{ left: left(hover ?? 0) + band }}>
          <div className="d">{fmtDay(active.date, 'long')}</div>
          <div>
            <b>{fmtInt(total(active))}</b> scans
          </div>
        </div>
      )}
    </div>
  );
}

export function WeeklyBars({ weeks }: { weeks: MorrisonsWeek[] }) {
  const [ref, width] = useWidth<HTMLDivElement>(400);
  const h = 220;
  const pad = { l: 8, r: 8, t: 24, b: 26 };
  const max = Math.max(1, ...weeks.map((w) => weekStickers(w) ?? 0)) * 1.12;
  const band = (width - pad.l - pad.r) / Math.max(1, weeks.length);
  const bw = Math.min(64, band * 0.7);
  const y = (v: number) => h - pad.b - (v / max) * (h - pad.b - pad.t);
  return (
    <div ref={ref} className="mo-chart">
      <svg width={width} height={h} role="img" aria-label="Stickers scanned per week">
        <line x1={pad.l} x2={width - pad.r} y1={h - pad.b} y2={h - pad.b} className="mo-axis" />
        {weeks.map((w, i) => {
          const v = weekStickers(w);
          const x = pad.l + band * i + (band - bw) / 2;
          const shape = v == null ? null : barPath(x, y(v), bw, h - pad.b);
          return (
            <g key={w.week}>
              <title>{`Week ${w.week}: ${v == null ? 'not started' : `${fmtInt(v)} stickers scanned`}`}</title>
              {shape ? (
                <path d={shape} className={w.status === 'live' ? 'mo-bar-hot' : 'mo-bar-new'} />
              ) : (
                <rect x={x} y={h - pad.b - 3} width={bw} height={3} rx={1.5} className="mo-bar-ghost" />
              )}
              <text x={x + bw / 2} y={v == null ? h - pad.b - 8 : y(v) - 6} textAnchor="middle" className="mo-bar-value">
                {v == null ? '—' : width < 420 ? fmtCompact(v) : fmtInt(v)}
              </text>
              <text x={x + bw / 2} y={h - 8} textAnchor="middle">
                {width < 360 ? `Wk${w.week}` : `Week ${w.week}`}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export type DonutPart = { key: string; label: string; value: number; color: string };

export function PrizeDonut({ parts }: { parts: DonutPart[] }) {
  const total = parts.reduce((a, p) => a + p.value, 0);
  const S = 150;
  // One stroked circle per part: the ring is the stroke, each part a dash of it.
  const radius = 56;
  const ring = 2 * Math.PI * radius;
  const gap = total > 0 && parts.filter((p) => p.value > 0).length > 1 ? 2 : 0;
  let offset = 0;
  return (
    <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} role="img" aria-label="Prize outcomes of opened flowpacks">
      <g transform={`rotate(-90 ${S / 2} ${S / 2})`}>
        <circle cx={S / 2} cy={S / 2} r={radius} className="mo-ring-empty" strokeWidth={22} fill="none" />
        {total > 0 &&
          parts
            .filter((p) => p.value > 0)
            .map((p) => {
              const len = (p.value / total) * ring;
              const dash = Math.max(0, len - gap);
              const el = (
                <circle
                  key={p.key}
                  cx={S / 2}
                  cy={S / 2}
                  r={radius}
                  fill="none"
                  stroke={p.color}
                  strokeWidth={22}
                  strokeDasharray={`${dash} ${ring - dash}`}
                  strokeDashoffset={-offset}
                >
                  <title>{`${p.label}: ${fmtInt(p.value)} (${fmtPct(share(p.value, total))})`}</title>
                </circle>
              );
              offset += len;
              return el;
            })}
      </g>
    </svg>
  );
}

/** The 120 cards on a grid, darker the more often they were collected; the five rarest outlined in red. */
export function CardHeatmap({ cards }: { cards: MorrisonsPayload['cards'] }) {
  const max = Math.max(1, ...cards.map((c) => c.collected));
  const any = cards.some((c) => c.collected > 0);
  const rarest = new Set(
    any
      ? [...cards]
          .sort((a, b) => a.collected - b.collected || a.card.localeCompare(b.card))
          .slice(0, 5)
          .map((c) => c.card)
      : [],
  );
  return (
    <div className="mo-heat" role="list" aria-label="New-card scans per card, 001 to 120">
      {cards.map((c) => (
        <div
          key={c.card}
          role="listitem"
          className={`mo-cell${rarest.has(c.card) ? ' rare' : ''}`}
          style={{ ['--k' as string]: String(c.collected / max) }}
          data-tip={`Card ${c.card}: ${fmtInt(c.collected)} collected`}
          aria-label={`Card ${c.card}: ${fmtInt(c.collected)} collected`}
        >
          {c.card}
        </div>
      ))}
    </div>
  );
}
